import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Inject,
    OnInit,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { KAL_DIALOG_DATA, KalDialogRef } from '@kalidea/kaligraphi';
import gql from 'graphql-tag';
import { merge, Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import { BaseComponent } from 'src/app/_core/tools/base.component';
import {
    FolderBankFilters
} from 'src/app/_shared/dialogs/folder-bank-dialog/folder-bank-filters/folder-bank-filters.component';
import {
    FolderBankListComponent
} from 'src/app/_shared/dialogs/folder-bank-dialog/folder-bank-list/folder-bank-list.component';
import { SharedFragment } from 'src/app/_shared/fragments/shared.fragment';
import {
    DocumentaryManagementRecordFragment
} from 'src/app/board/web-administration/fragments/documentary-management-record.fragment';
import {
    GedCriteresFiltrageGedModelInput,
    GedListeAlbumsGedModel,
    MutationGetListeAlbumsGedModelArgs,
    MutationSaveAlbumGedArgs,
    PortailAlbumDto
} from 'src/graphql/generated-types';

export type PartialFolderBankFilters = Partial<Record<keyof FolderBankFilters, boolean>>;

export interface FolderBankListDialogData {
    accepted?: boolean;

    selectedFolder?: PortailAlbumDto;

    shouldDisableSaveDocument?: boolean;
}

@Component({
    selector: 'app-folder-bank-list-dialog',
    templateUrl: './folder-bank-list-dialog.component.html',
    styleUrls: ['./folder-bank-list-dialog.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FolderBankListDialogComponent extends BaseComponent implements OnInit {

    form: FormGroup;

    selectedFolder: PortailAlbumDto = null;

    shouldDisplayLocalFile = false;

    @ViewChild(FolderBankListComponent) listComponent: FolderBankListComponent;

    generatedFolder: PortailAlbumDto;


    constructor(private readonly dialogRef: KalDialogRef<FolderBankListDialogComponent>,
                private readonly graphqlService: GraphqlService,
                private readonly fb: FormBuilder,
                @Inject(KAL_DIALOG_DATA) readonly data: FolderBankListDialogData,
                protected readonly cdr: ChangeDetectorRef
    ) {
        super(cdr);
    }

    get isOpenedFilters(): boolean {
        return !!this.listComponent?.isOpenedFilters;
    }

    submit(): void {
        if (this.form.value.localFile && !this.data.shouldDisableSaveDocument) {
            this.watch$ = this.wrapLoader$(this.saveLocalFolder$()).pipe(
                tap(() => {
                    this.dialogRef.close({
                        accepted: true,
                        selectedFolder: this.generatedFolder,
                    });
                })
            );
        } else {
            this.dialogRef.close({
                accepted: true,
                selectedFolder: this.selectedFolder,
            });
        }
    }

    close(): void {
        this.dialogRef.close({accepted: false});
    }

    toggleAddLocalFile(): void {
        this.shouldDisplayLocalFile = !this.shouldDisplayLocalFile;
    }

    private saveLocalFolder$(): Observable<GedListeAlbumsGedModel> {
        const {
            IdStatut,
            ...folderToSave
        } = this.form.value.localFile;

        return this.graphqlService.mutateOne<'SaveAlbumGED', MutationSaveAlbumGedArgs>({
            mutation: gql`
                mutation SaveAlbumGED(
                    $contexte: ContextInfosInput
                    $album: PortailAlbumDTOInput
                    $SupprimerFichierLies: Boolean
                ) {
                    SaveAlbumGED(
                        contexte: $contexte
                        album: $album
                        SupprimerFichierLies: $SupprimerFichierLies
                    ) {
                        ...ListeAlbumsFragment
                    }
                }
                ${DocumentaryManagementRecordFragment.recordListFragment}
            `,
            variables: {
                album: folderToSave,
                SupprimerFichierLies: false
            }
        }).pipe(
            map((folders: GedListeAlbumsGedModel) => folders?.NbElementTot || 0),
            switchMap((nbElmtTotal: number) =>
                this.getListeAlbumsGEDModel({
                    OrderByID: true,
                    Skip: Math.max(0, nbElmtTotal - 20),
                    Take: 20
                })
            ),
            tap((folders: GedListeAlbumsGedModel) => {
                this.generatedFolder = folders.ListeAlbums.PortailAlbumDTO
                    .sort((a, b) => a.Id === b.Id ? 0 : (a.Id > b.Id ? -1 : 1))
                    .find(e => e.Libelle === this.selectedFolder.Libelle.slice(0));

                this.selectedFolder.Id = this.generatedFolder.Id;
            })
        );
    }

    private getListeAlbumsGEDModel(criteres: GedCriteresFiltrageGedModelInput): Observable<GedListeAlbumsGedModel> {
        return this.graphqlService.mutateOne<'getListeAlbumsGEDModel', MutationGetListeAlbumsGedModelArgs>({
            mutation: gql`
            mutation getListeAlbumsGEDModel($contexte: ContextInfosInput, $criteres: GEDCriteresFiltrageGEDModelInput) {
                getListeAlbumsGEDModel(contexte: $contexte, criteres: $criteres) {
                    ... folders
                }
            }
             ${SharedFragment.folders}`,
            variables: {criteres}
        });
    }

    private initSubscription(): void {
        const {
            localFile,
            fileFromList
        } = this.form.controls;

        this.watch$ = merge(
            this.form.valueChanges.pipe(
                tap(({localFile, fileFromList}) => {

                    const {
                        IdStatut,
                        IdCategorie,
                        ...realLocalFile
                    } = localFile || {};

                    this.selectedFolder = fileFromList || realLocalFile;
                })
            ),
            localFile.valueChanges.pipe(
                tap(() => fileFromList.reset(null, {emitEvent: false}))
            ),
            fileFromList.valueChanges.pipe(
                tap(() => localFile.reset(null, {emitEvent: false}))
            )
        );
    }

    ngOnInit(): void {
        this.form = this.fb.group({
            localFile: [null],
            fileFromList: [null]
        });

        this.initSubscription();
    }

}
